// src/components/Partner/PartnerList.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table,
  Input,
  Button,
  Badge,
  Label,
  Row,
  Col,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import NewPartnerModal from "./NewPartnerModal";
import ConfirmPartnerRemovalModal from "./ConfirmPartnerRemovalModal.js";
import Moment from "moment";
import axios from "axios";
import { API_URL_partners } from "../../constants";

import { getAuthHeaders } from "../../utils/auth";
// Custom hook for debouncing
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

// Custom hook for expanded rows
const useExpandedRows = () => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = useCallback((id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const isExpanded = useCallback(
    (id) => expandedRows.has(id),
    [expandedRows]
  );

  return { toggleRow, isExpanded };
};

const PartnerList = ({
  partners = [],
  resetState,
  itemsPerPage = 8,
  onFilteredChange,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [grupoFilter, setGrupoFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("Activo");
  const [page, setPage] = useState(1);
  const [isTogglingStatus, setIsTogglingStatus] = useState(null);

  const { toggleRow, isExpanded } = useExpandedRows();
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Helper to get partner ID
  const getPartnerId = useCallback(
    (partner) => partner.id ?? partner.pk ?? partner.partner_code,
    []
  );

  // Unique grupos for dropdown
  const grupos = useMemo(() => {
    const set = new Set();
    (partners || []).forEach((p) => {
      if (p.grupo) set.add(p.grupo);
    });
    return Array.from(set).sort();
  }, [partners]);

  // Filter logic with status
  const filteredRows = useMemo(() => {
    let rows = partners || [];

    // Status filter
    if (statusFilter) {
      const filterStatusLower = statusFilter.toLowerCase();
      rows = rows.filter((p) => {
        const partnerStatus = (p.status || "Activo").toLowerCase();
        return partnerStatus === filterStatusLower;
      });
    }

    // Search filter
    if (debouncedSearchTerm.trim()) {
      const term = debouncedSearchTerm.toLowerCase();
      rows = rows.filter((p) => {
        return (
          (p.partner && p.partner.toLowerCase().includes(term)) ||
          (p.partner_code && p.partner_code.toLowerCase().includes(term)) ||
          (p.email && p.email.toLowerCase().includes(term)) ||
          (p.localizacion && p.localizacion.toLowerCase().includes(term)) ||
          (p.nivel && p.nivel.toLowerCase().includes(term)) ||
          (p.grupo && p.grupo.toLowerCase().includes(term))
        );
      });
    }

    // Grupo filter
    if (grupoFilter) {
      rows = rows.filter((p) => p.grupo === grupoFilter);
    }

    return rows;
  }, [partners, debouncedSearchTerm, grupoFilter, statusFilter]);

  // Notify parent when filters update
  useEffect(() => {
    if (typeof onFilteredChange === "function") {
      onFilteredChange(filteredRows);
    }
  }, [filteredRows, onFilteredChange]);

  // Pagination
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredRows.length / itemsPerPage)),
    [filteredRows.length, itemsPerPage]
  );

  const safePage = useMemo(
    () => Math.min(Math.max(page, 1), totalPages),
    [page, totalPages]
  );

  const currentPageRows = useMemo(() => {
    const start = (safePage - 1) * itemsPerPage;
    return filteredRows.slice(start, start + itemsPerPage);
  }, [filteredRows, safePage, itemsPerPage]);

  // Event handlers
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  }, []);

  const handleGrupoFilterChange = useCallback((e) => {
    setGrupoFilter(e.target.value);
    setPage(1);
  }, []);

  const handleStatusFilterChange = useCallback((e) => {
    setStatusFilter(e.target.value);
    setPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setGrupoFilter("");
    setStatusFilter("Activo");
    setPage(1);
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Toggle partner status
  const handleStatusToggle = async (partner) => {
    const partnerId = getPartnerId(partner);
    if (!partnerId) {
      console.error("Partner missing id:", partner);
      return;
    }

    const currentStatus = (partner.status || "Activo").toString();
    const nextStatus = currentStatus === "Activo" ? "Inactivo" : "Activo";

    setIsTogglingStatus(partnerId);

    try {
      const base = String(API_URL_partners);
      const url = base.endsWith("/")
        ? `${base}${partnerId}/`
        : `${base}/${partnerId}/`;

      const headers = getAuthHeaders();
      await axios.patch(
        url,
        { status: nextStatus },
        {
          headers: {
            ...headers,
            Accept: "application/json",
          },
          timeout: 10000,
        }
      );

      if (typeof resetState === "function") {
        resetState();
      }
    } catch (err) {
      console.error("Error toggling status:", err);
    } finally {
      setIsTogglingStatus(null);
    }
  };

  // Render expandable cell
  const renderCell = useCallback(
    (partnerId, text, style = {}) => {
      const expanded = isExpanded(partnerId);
      const displayText = text || "";

      return (
        <td style={style} title={displayText}>
          <div
            className={`text-break ${expanded ? "" : "text-truncate"}`}
            role="button"
            tabIndex={0}
            aria-expanded={expanded}
            aria-label={
              expanded ? "Click to collapse" : "Click to expand full text"
            }
            onClick={() => toggleRow(partnerId)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleRow(partnerId);
              }
            }}
            style={{ cursor: "pointer", maxWidth: "200px" }}
          >
            {displayText}
          </div>
        </td>
      );
    },
    [isExpanded, toggleRow]
  );

  // Status badge component
  const StatusBadge = useCallback(
    ({ partner }) => {
      const partnerId = getPartnerId(partner);
      const currentStatus = partner.status || "Activo";
      const isToggling = isTogglingStatus === partnerId;

      return (
        <div className="d-flex align-items-center justify-content-center gap-2">
          <div className="form-check form-switch">
            <Input
              type="switch"
              role="switch"
              className="form-check-input"
              checked={currentStatus === "Activo"}
              onChange={() => handleStatusToggle(partner)}
              disabled={isToggling}
              aria-label={`Toggle status for ${partner.partner}`}
            />
          </div>
          <Badge
            color={
              currentStatus === "Activo"
                ? "success"
                : currentStatus === "Inactivo"
                ? "secondary"
                : "warning"
            }
          >
            {isToggling ? "..." : currentStatus}
          </Badge>
        </div>
      );
    },
    [isTogglingStatus, getPartnerId, handleStatusToggle]
  );

  // Pagination controls
  const PaginationControls = useMemo(() => {
    if (totalPages <= 1) return null;

    const maxButtons = 5;
    let startPage = Math.max(1, safePage - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);

    if (endPage - startPage < maxButtons - 1) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    const pageButtons = [];
    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <PaginationItem key={i} active={safePage === i}>
          <PaginationLink
            onClick={() => handlePageChange(i)}
            aria-label={`Go to page ${i}`}
            aria-current={safePage === i ? "page" : undefined}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return (
      <Pagination className="mt-3" aria-label="Partner list pagination">
        <PaginationItem disabled={safePage === 1}>
          <PaginationLink
            previous
            onClick={() => handlePageChange(safePage - 1)}
            aria-label="Go to previous page"
          />
        </PaginationItem>

        {startPage > 1 && (
          <>
            <PaginationItem>
              <PaginationLink onClick={() => handlePageChange(1)}>
                1
              </PaginationLink>
            </PaginationItem>
            {startPage > 2 && (
              <PaginationItem disabled>
                <PaginationLink>...</PaginationLink>
              </PaginationItem>
            )}
          </>
        )}

        {pageButtons}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && (
              <PaginationItem disabled>
                <PaginationLink>...</PaginationLink>
              </PaginationItem>
            )}
            <PaginationItem>
              <PaginationLink onClick={() => handlePageChange(totalPages)}>
                {totalPages}
              </PaginationLink>
            </PaginationItem>
          </>
        )}

        <PaginationItem disabled={safePage === totalPages}>
          <PaginationLink
            next
            onClick={() => handlePageChange(safePage + 1)}
            aria-label="Go to next page"
          />
        </PaginationItem>
      </Pagination>
    );
  }, [totalPages, safePage, handlePageChange]);

  return (
    <div>
      {/* Filters */}
      <Row className="mb-3">
        <Col md={3}>
          <Label htmlFor="status-filter">Status</Label>
          <Input
            type="select"
            id="status-filter"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            aria-label="Filter by status"
          >
            <option value="">Todos</option>
            <option value="Activo">Activo</option>
            <option value="Inactivo">Inactivo</option>
            <option value="Prospecto">Prospecto</option>
          </Input>
        </Col>

        <Col md={3}>
          <Label htmlFor="grupo-filter">Value Stream</Label>
          <Input
            type="select"
            id="grupo-filter"
            value={grupoFilter}
            onChange={handleGrupoFilterChange}
            aria-label="Filter by Value Stream"
          >
            <option value="">Todos los Value Streams</option>
            {grupos.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </Input>
        </Col>

        <Col md={4}>
          <Label htmlFor="search-input">Buscar</Label>
          <Input
            type="text"
            id="search-input"
            placeholder="Buscar por nombre, código, email..."
            value={searchTerm}
            onChange={handleSearchChange}
            aria-label="Search partners"
          />
          {debouncedSearchTerm !== searchTerm && (
            <small className="text-muted">Buscando...</small>
          )}
        </Col>

        <Col md={2} className="d-flex align-items-end">
          <Button
            color="secondary"
            onClick={handleClearFilters}
            className="w-100"
          >
            Limpiar
          </Button>
        </Col>
      </Row>

      {/* Results summary */}
      <div className="mb-2">
        <small className="text-muted">
          Mostrando {currentPageRows.length} de {filteredRows.length} miembro(s)
          {filteredRows.length !== partners.length &&
            ` (filtrado de ${partners.length} total)`}
        </small>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <Table striped hover className="align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ minWidth: "120px" }}>Status</th>
              <th style={{ minWidth: "120px" }}>Código</th>
              <th style={{ minWidth: "180px" }}>Miembro</th>
              <th style={{ minWidth: "100px" }}>Nivel</th>
              <th style={{ minWidth: "200px" }}>Email</th>
              <th style={{ minWidth: "150px" }}>Value Stream</th>
              <th style={{ minWidth: "150px" }}>Localización</th>
              <th style={{ minWidth: "140px" }}>Fecha Ingreso</th>
              <th style={{ minWidth: "120px" }}>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {currentPageRows.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center py-5">
                  {partners.length === 0 ? (
                    <div>
                      <h5>No hay miembros registrados</h5>
                      <p className="text-muted">
                        Comienza agregando tu primer miembro
                      </p>
                    </div>
                  ) : (
                    <div>
                      <h5>No se encontraron resultados</h5>
                      <p className="text-muted">
                        Intenta ajustar los filtros de búsqueda
                      </p>
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              currentPageRows.map((p) => {
                const partnerId = getPartnerId(p);

                return (
                  <tr key={partnerId}>
                    <td>
                      <StatusBadge partner={p} />
                    </td>
                    {renderCell(partnerId, p.partner_code)}
                    {renderCell(partnerId, p.partner)}
                    {renderCell(partnerId, p.nivel)}
                    {renderCell(partnerId, p.email)}
                    {renderCell(partnerId, p.grupo)}
                    {renderCell(partnerId, p.localizacion)}
                    <td>
                      {p.ingreso_date
                        ? Moment(p.ingreso_date).format("MMM DD, YYYY")
                        : "—"}
                    </td>
                    <td className="text-nowrap">
                      <div className="d-flex gap-1">
                        <NewPartnerModal
                          create={false}
                          partner={p}
                          resetState={resetState}
                        />
                        <ConfirmPartnerRemovalModal
                          pk={partnerId}
                          resetState={resetState}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      {PaginationControls}
    </div>
  );
};

export default PartnerList;